import React from 'react';
import styled from 'styled-components';
import { getPersonalities } from '../data/personalities';

const Container = styled.div`
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
`;

const Title = styled.h2`
  color: #e91e63;
  margin-bottom: 2.5rem;
  text-align: center;
`;

const BackLink = styled.button`
  display: block;
  margin: 0 auto 1.5rem;
  background: none;
  border: none;
  color: #666;
  font-size: 0.9rem;
  text-decoration: underline;

  &:hover {
    color: #e91e63;
  }
`;

const PersonalityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const PersonalityCard = styled.div`
  border: ${({ selected }) => (selected ? '2px solid #e91e63' : '1px solid #ddd')};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${({ selected }) => (selected ? '#fff5f8' : 'white')};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
`;

const PersonalityName = styled.h3`
  margin-top: 0;
  color: #e91e63;
  font-size: 1.1rem;
`;

// Descriptive difficulty, shown as filled stars out of 3: how hard this
// personality is to hold a good conversation with (Easy = they carry it,
// Hard = they hold back).
const DIFFICULTY_STARS = { Easy: 1, Medium: 2, Hard: 3 };

const Stars = styled.span`
  flex-shrink: 0;
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  white-space: nowrap;
`;

const FilledStar = styled.span`
  color: #e6a817;
`;

const EmptyStar = styled.span`
  color: #ddd;
`;

// Three stars, filled to the level's rating — shared by the cards and the legend.
const difficultyStars = (level) =>
  Array.from({ length: 3 }, (_, i) =>
    i < (DIFFICULTY_STARS[level] || 0) ? (
      <FilledStar key={i}>★</FilledStar>
    ) : (
      <EmptyStar key={i}>★</EmptyStar>
    )
  );

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1.5rem;
  margin-bottom: 1.75rem;
  font-size: 0.8rem;
  color: #888;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const PersonalityDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  background-color: #e91e63;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #d81b60;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// The personality grid is Free Practice's own step: you choose exactly who you
// want to chat with. (Quick Practice and Date in Eight randomize, so they skip
// this screen entirely.)
const PersonalitySelection = ({ gender, onSelectPersonality, onBack }) => {
  const [selectedPersonality, setSelectedPersonality] = React.useState(null);
  // Show the grid easiest-first so a newcomer's eye lands on an approachable
  // character. Stable sort keeps each personality's authored order within a tier.
  const personalities = [...getPersonalities(gender)].sort(
    (a, b) => (DIFFICULTY_STARS[a.difficulty] || 0) - (DIFFICULTY_STARS[b.difficulty] || 0)
  );

  const handleSelectPersonality = (personality) => {
    setSelectedPersonality(personality);
  };

  const handleStartPractice = () => {
    if (selectedPersonality) {
      onSelectPersonality(selectedPersonality);
    }
  };

  return (
    <Container>
      {onBack && <BackLink onClick={onBack}>&larr; Back to modes</BackLink>}
      <Title>Pick a personality for your Free Practice chat</Title>
      <Legend>
        {['Easy', 'Medium', 'Hard'].map((level) => (
          <LegendItem key={level}>
            <Stars>{difficultyStars(level)}</Stars>
            {level}
          </LegendItem>
        ))}
      </Legend>
      <PersonalityGrid>
        {personalities.map((personality) => (
          <PersonalityCard
            key={personality.id}
            selected={selectedPersonality?.id === personality.id}
            onClick={() => handleSelectPersonality(personality)}
          >
            <CardHeader>
              <PersonalityName>{personality.name}</PersonalityName>
              {personality.difficulty && (
                <Stars
                  title={`${personality.difficulty} difficulty`}
                  aria-label={`${personality.difficulty} difficulty`}
                >
                  {difficultyStars(personality.difficulty)}
                </Stars>
              )}
            </CardHeader>
            <PersonalityDescription>{personality.description}</PersonalityDescription>
          </PersonalityCard>
        ))}
      </PersonalityGrid>
      <ButtonContainer>
        <Button onClick={handleStartPractice} disabled={!selectedPersonality}>
          Start Free Practice
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default PersonalitySelection;
